/* global _main   */
import { doAjaxPromise, getRemoteHost, isNATed} from './api_request';
import { SuccessOrError, MoveRelativeCombinedPayload, MoveToPositionCombinedPayload, ErrorResponse, SetPositionPayload } from './models';
import { Configuration, AxisDetails, InputDetails, OutputDetails, PneumaticDetails } from './models/configuration';

// const _fetch = fetch;
const _fetch = doAjaxPromise;

function getConnectionString(): string {
  return getRemoteHost() + '/v1/immediate';
}

function post<TBodyData extends object>(path: string, value: TBodyData): Promise<void | Response> {
  if (!path.startsWith('/')) path = '/' + path;
  return _fetch(`${getConnectionString()}${path}`, {
    method: "POST",
    body: JSON.stringify(value),
    headers: lHeaders
  });
}

function get<TQueryParameters extends Record<string, string>>(path: string, queryParameters?: TQueryParameters): Promise<void | Response> {
  const url = new URL(`/v1/immediate${path}`, getRemoteHost());
  for (const [paramKey, paramValue] of Object.entries(queryParameters || {})) {
    url.searchParams.append(paramKey, paramValue);
  }

  return _fetch(url.toString());
}

/**
 * If the response is not 'ok' the Promise is rejected. Otherwise, we return the json result.
 * @param {Response} pResponse
 * @returns {Promise<any>}
 */
function jsonOrReject(pResponse) {
  if (!pResponse.ok) {
    // Logging
    if (pResponse.status === 400) {
      console.error('Failed to process request. Message: ' + pResponse.statusText, pResponse);
    }
    else {
      console.error('Failed to process request. Message: ' + pResponse);
    }
    // Will trigger catch block
    return Promise.reject(pResponse);
  }

  // Will trigger then block
  return pResponse.json();
}

const lHeaders = { "Content-type": "application/json; charset=UTF-8" };

function getSmartDriveStatus(pControllerId) {
  return _fetch(getConnectionString() + '/get_smartdrive_status?controllerId=' + pControllerId).then(jsonOrReject);
}

function tryClearDriveErrors() {
  return _fetch(getConnectionString() + '/try_clear_drive_errors').then(jsonOrReject);
}

function isMotorDetected(axisUuid) {
  return _fetch(getConnectionString() + '/is_motor_detected?axisUuid=' + axisUuid).then(jsonOrReject);
}

function getSpeed(pAxisUuid: string) {
  return _fetch(getConnectionString() + '/get_speed?axisUuid=' + pAxisUuid).then(jsonOrReject);
}

function getMaxSpeed(axisUuid: string): Promise<{ maxSpeed: number }> {
  return get('/get_max_speed', { axisUuid }).then(jsonOrReject);
}

function setSpeed(pAxisUuid, pSpeed) {
  return _fetch(getConnectionString() + "/set_speed", {
    method: "POST",
    body: JSON.stringify({
      axisUuid: pAxisUuid,
      newSpeed: pSpeed
    }),
    headers: lHeaders
  }).then(jsonOrReject);
}

function getAcceleration(pAxisUuid) {
  return _fetch(getConnectionString() + '/get_acceleration?axisUuid=' + pAxisUuid).then(jsonOrReject);
}

function setAcceleration(pAxisUuid, pAcceleration) {
  return _fetch(getConnectionString() + "/set_acceleration", {
    method: "POST",
    body: JSON.stringify({
      axisUuid        : pAxisUuid,
      newAcceleration : pAcceleration
    }),
    headers: lHeaders
  }).then(jsonOrReject);
}

function moveRelative(pAxisUuid, pDistance, isBlindMove = false, waitForMotionCompletion = true) {
  return _fetch(getConnectionString() + "/move_relative", {
    method: "POST",
    body: JSON.stringify({
      axisUuid                  : pAxisUuid,
      distance                  : pDistance,
      isBlindMove               : isBlindMove,
      waitForMotionCompletion   : waitForMotionCompletion
    }),
    headers: lHeaders
  }).then(jsonOrReject);
}

function moveRelativeCombined(moveRelativePayloadList: MoveRelativeCombinedPayload[], waitForMotionCompletion = true): Promise<SuccessOrError> {
  return post('/move_relative_combined', { moveRelativePayloadList, waitForMotionCompletion }).then(jsonOrReject);
}

function moveToPosition(axisUuid: string, position: string, waitForMotionCompletion = true) {
  return _fetch(getConnectionString() + "/move_to_position", {
    method: "POST",
    body: JSON.stringify({
      axisUuid,
      position,
      waitForMotionCompletion
    }),
    headers: lHeaders
  }).then(jsonOrReject);
}

function moveToPositionCombined(moveToPositionPayloadList: MoveToPositionCombinedPayload[], waitForMotionCompletion = true): Promise<SuccessOrError> {
  return post('/move_to_position_combined', { moveToPositionPayloadList, waitForMotionCompletion }).then(jsonOrReject);
}

function home(axisUuid: string, waitForMotionCompletion = true) {
  return post('/home', { axisUuid, waitForMotionCompletion }).then(jsonOrReject);
}

function homeAll(axisUuidList: string[]): Promise<SuccessOrError> {
  return post('/home_all', { axisUuidList }).then(jsonOrReject);
}

function end(pAxisUuid) {
  return _fetch(getConnectionString() + "/end", {
    method: "POST",
    body: JSON.stringify({
      axisUuid    : pAxisUuid,
    }),
    headers: lHeaders
  }).then(jsonOrReject);
}

function stop(pControllerId) {
  return _fetch(getConnectionString() + "/stop_motion_on_controller", {
    method: "POST",
    body: JSON.stringify({
      controllerId  : pControllerId,
    }),
    headers: lHeaders
  }).then(jsonOrReject);
}

function halt(pAxisUuid) {
  return _fetch(getConnectionString() + "/halt", {
    method: "POST",
    body: JSON.stringify({
      axisUuid: pAxisUuid
    }),
    headers: lHeaders
  }).then(jsonOrReject);
}

function isMotionComplete(pControllerId) {
  return _fetch(getConnectionString() + '/is_motion_complete?controllerId=' + encodeURIComponent(pControllerId)).then(jsonOrReject);
}

function isAxisMotionComplete(pAxisUuid) {
  return _fetch(getConnectionString() + '/is_axis_motion_complete?axisUuid=' + encodeURIComponent(pAxisUuid)).then(jsonOrReject);
}

/**
 * Waits for motion completion on the provided set of axes. If none are provided,
 * we wait for motion completion on the entire system.
 * @param axisUuidList Optional list of axes to wait on.
 * @returns Success or an error.
 */
function waitForMotionCompletion(axisUuidList?: string[]): Promise<SuccessOrError> {
  return _fetch(`${getConnectionString()}/wait_for_motion_completion`, {
    method: 'GET',
    body: JSON.stringify({
      axisUuidList
    }),
    headers: lHeaders
  }).then(jsonOrReject);
}

/**
 *
 * @param {string} pAxisUuid
 * @param { {homeport: "A"|"B"} } payload
 * @returns
 */
function setEndstopConfig(pAxisUuid, pPayload) {
  return _fetch(getConnectionString() + "/set_endstop_config", {
    method: "POST",
    body: JSON.stringify({
      axisUuid: pAxisUuid,
      payload: pPayload
    }),
    headers: lHeaders
  }).then(jsonOrReject);
}

function getEndstopState(pControllerId: string) {
  return _fetch(getConnectionString() + '/get_endstop_state?controllerId=' + encodeURIComponent(pControllerId))
    .then(jsonOrReject);
}

function getEndStopStateForAxis(axisUuid: string): Promise<{home: boolean; end: boolean}> {
  return _fetch(getConnectionString() + '/get_endstop_state_for_axis?axisUuid=' + encodeURIComponent(axisUuid))
    .then(jsonOrReject);
}

function getEndstopConfig(pControllerId, pAxisDriverNumber) {
  return _fetch(getConnectionString() + '/get_endstop_config?controllerId=' + encodeURIComponent(pControllerId) + "&axisDriveNumber=" + encodeURIComponent(pAxisDriverNumber))
    .then(jsonOrReject);
}

function getBrakeState(pAxisUuid) {
  return _fetch(getConnectionString() + '/brakes?axisUuid=' + encodeURIComponent(pAxisUuid))
    .then(jsonOrReject);
}

function lockBrakes(pAxisUuid) {
  return _fetch(getConnectionString() + "/brakes/lock", {
    method: "POST",
    body: JSON.stringify({
      axisUuid  : pAxisUuid,
    }),
    headers: lHeaders
  }).then(jsonOrReject);
}

function unlockBrakes(pAxisUuid) {
  return _fetch(getConnectionString() + "/brakes/unlock", {
    method: "POST",
    body: JSON.stringify({
      axisUuid  : pAxisUuid,
    }),
    headers: lHeaders
  }).then(jsonOrReject);
}

function unlockAllBrakes(pControllerId) {
  return _fetch(getConnectionString() + "/brakes/all_unlock", {
    method: "POST",
    body: JSON.stringify({
      controllerId  : pControllerId,
    }),
    headers: lHeaders
  }).then(jsonOrReject);
}

function getIoState(pControllerId) {
  return _fetch(getConnectionString() + '/io?controllerId=' + encodeURIComponent(pControllerId)).then(jsonOrReject);
}

/**
 * Read a digital input provided the uuid.
 * @param inputUuid ID of the input
 * @returns A promise containing the current value of the input
 */
function readInput(inputUuid: string): Promise<{ value: number }> {
  return _fetch(`${getConnectionString()}/io/input?inputUuid=${inputUuid}`).then(jsonOrReject);
}

/**
 * Write an output via the uuid of the item.
 * @param outputUuid Output ID
 * @param value Value to write
 * @returns Success or error
 */
function writeOutput(outputUuid: string, value: 1 | 0): Promise<SuccessOrError> {
  return _fetch(getConnectionString() + "/io/output", {
    method: "POST",
    body: JSON.stringify({ outputUuid, value }),
    headers: lHeaders
  }).then(jsonOrReject);
}

/**
 * Write a digital output given its controller id, device, and pin number. Prefer using 'writeOutput' if possible.
 * @param pControllerId The controller that the output belongs to
 * @param pDevice The device number
 * @param pPin The pin number
 * @param pValue The value
 * @returns Success or error
 */
function writeDigitalOutputRaw(pControllerId: string, pDevice: number, pPin: number, pValue: 1 | 0): Promise<SuccessOrError> {
  return _fetch(getConnectionString() + "/io/output/raw", {
    method: "POST",
    body: JSON.stringify({
      controllerId  : pControllerId,
      device        : pDevice,
      pin           : pPin,
      value         : pValue
    }),
    headers: lHeaders
  }).then(jsonOrReject);
}

/**
 * Write a power switch given its controller id and device number. Prefer using 'writeOutput' if possible.
 * @param pControllerId The controller that the output belongs to
 * @param pDevice The device number
 * @param pValue The new value
 * @returns Success or error
 */
function writePowerSwitchRaw(pControllerId: string, pDevice: number, pValue: "on" | "off"): Promise<SuccessOrError> {
  return _fetch(getConnectionString() + "/io/powerSwitch/raw", {
    method: "POST",
    body: JSON.stringify({
      controllerId  : pControllerId,
      device        : pDevice,
      value         : pValue
    }),
    headers: lHeaders
  }).then(jsonOrReject);
}

function getAllPositionsForController(pControllerId) {
  return _fetch(getConnectionString() + '/all_positions?controllerId=' + encodeURIComponent(pControllerId)).then(jsonOrReject);
}

function getAllCurrentSpeedsForController(pControllerId) {
  return _fetch(getConnectionString() + '/all_current_speeds?controllerId=' + encodeURIComponent(pControllerId)).then(jsonOrReject);
}

function loadPath(pPath, pAcceleration, pSpeedOverride, pScaleMultiple, pDryRun, controllerUuid ) {
  return fetch(getConnectionString() + "/path", {
    method: "POST",
    body: JSON.stringify({
      path: pPath,
      acceleration: pAcceleration,
      speedOverride: pSpeedOverride,
      scaleMultiple: pScaleMultiple,
      dryRun: pDryRun,
      controllerUuid: controllerUuid
    }),
    headers: lHeaders
  }).then(jsonOrReject);
}

function startPath(pWaitOnPathCompletion, controllerUuid) {
  return fetch(getConnectionString() + "/path/start", {
    method: "POST",
    body: JSON.stringify({
      waitOnPathCompletion: pWaitOnPathCompletion,
      controllerUuid: controllerUuid
    }),
    headers: lHeaders
  }).then(jsonOrReject);
}

function stopPath(controllerUuid) {
  return fetch(getConnectionString() + "/path/stop", {
    method: "POST",
    body: JSON.stringify({
      controllerUuid: controllerUuid
    }),
    headers: lHeaders
  }).then(jsonOrReject);
}

function parseGCode(pPath, pTimeStep, pAcceleration, controllerUuid) {
  return fetch(getConnectionString() + "/path/parse", {
    method: "POST",
    body: JSON.stringify({
      path: pPath,
      timeStep: pTimeStep,
      acceleration: pAcceleration,
      controllerUuid: controllerUuid
    }),
    headers: lHeaders
  }).then(jsonOrReject);
}

function getPathStatus(controllerUuid) {
  return fetch(getConnectionString() + "/path/status", {
    method: "POST",
    body: JSON.stringify({
      controllerUuid: controllerUuid
    }),
    headers: lHeaders
  }).then(jsonOrReject);
}

function addPathTool(pToolId, pTool) {
  return fetch(getConnectionString() + "/path/add_tool", {
    method: "POST",
    body: JSON.stringify({
      toolId: pToolId,
      tool: pTool,
    }),
    headers: lHeaders
  }).then(jsonOrReject);
}

function getPathTool(pToolId, controllerUuid) {
  return fetch(getConnectionString() + "/path/get_tool", {
    method: "POST",
    body: JSON.stringify({
      toolId: pToolId,
      controllerUuid: controllerUuid
    }),
    headers: lHeaders
  }).then(jsonOrReject);
}

function getAllPathTools(controllerUuid) {
  return fetch(getConnectionString() + "/path/get_all_tools", {
    method: "POST",
    body: JSON.stringify({
      controllerUuid: controllerUuid
    }),
    headers: lHeaders
  }).then(jsonOrReject);
}


function deletePathTool(pToolId, controllerUuid) {
  return fetch(getConnectionString() + "/path/delete_tool", {
    method: "POST",
    body: JSON.stringify({
      toolId: pToolId,
      controllerUuid: controllerUuid
    }),
    headers: lHeaders
  }).then(jsonOrReject);
}

function setPathAxisMap(pAxisMap) {
  return fetch(getConnectionString() + "/path/set_axis_map", {
    method: "POST",
    body: JSON.stringify({
      axisMap: pAxisMap,
    }),
    headers: lHeaders
  }).then(jsonOrReject);
}

function getPathAxisMap(controllerUuid) {
  return fetch(getConnectionString() + "/path/get_axis_map", {
    method: "POST",
    body: JSON.stringify({
      controllerUuid: controllerUuid
    }),
    headers: lHeaders
  }).then(jsonOrReject);
}

/**
 * Start a continuous move on an axis.
 * @param {string} pAxisUuid Axis UUID
 * @param {number} [pSpeed] Speed of the axis, default is 100.
 * @param {number} [pAcceleration] Acceleration of the axis, default is 100.
 * @returns {Promise<any>}
 */
function setContinuousMove(pAxisUuid, pSpeed, pAcceleration) {
  return _fetch(`${getConnectionString()}/set_continuous_move`, {
    method: "POST",
    body: JSON.stringify({
      axisUuid: pAxisUuid,
      speed: pSpeed,
      acceleration: pAcceleration
    }),
    headers: lHeaders
  }).then(jsonOrReject);
}

function stopContinuousMove(axisUuid: string, acceleration: number): Promise<{ success: boolean}> {
  return _fetch(`${getConnectionString()}/set_continuous_move`, {
    method: "POST",
    body: JSON.stringify({ axisUuid, acceleration }),
    headers: lHeaders
  }).then(jsonOrReject);
}

function getAxisPosition(axisUuid: string) {
  return _fetch(`${getConnectionString()}/axis_position?axisUuid=${axisUuid}`).then(jsonOrReject);
}

function setAxesPositions(axisUuidAndPositionList: SetPositionPayload[]): Promise<SuccessOrError> {
  return post('/set_axes_positions', { axisUuidAndPositionList }).then(jsonOrReject);
}

/*
 * @typedef {Object} Item
 * @property {string} uuid - Unique identifier
 * @property {string} friendlyName - Friendly name
 */

/**
 * @typedef {Object} Controller
 * @property {string} uuid - Unique identifier
 * @property {string} ipAddress - IP of the controller
 * @property {string} friendlyName - Friendly name
 * @property {Item[]} axisList - Axes on controller
 * @property {Item[]} inputList - Inputs on controller
 * @property {Item[]} outputList - Outputs on controller
 * @property {Item[]} pneumaticList - Pneumatics on controller
 */

/**
 * @typedef {Object} Configuration
 * @property {Controller[]} controllerList
 */

/**
 * The MachineLogic configuration in a form that is consumable by the ImmediateApi.
 * @returns {Configuration}
 */
function getConfiguration(): Promise<Configuration | ErrorResponse> {
  return get('/configuration').then(jsonOrReject);
}

function getAxisDetails(axisUuid: string): Promise<AxisDetails | ErrorResponse> {
  return get('/configuration/axis', { axisUuid }).then(jsonOrReject);
}

function getInputDetails(inputUuid: string): Promise<InputDetails | ErrorResponse> {
  return get('/configuration/input', { inputUuid }).then(jsonOrReject);
}

function getOutputDetails(outputUuid: string): Promise<OutputDetails | ErrorResponse> {
  return get('/configuration/output', { outputUuid }).then(jsonOrReject);
}

function getPneumaticDetails(pneumaticUuid: string): Promise<PneumaticDetails | ErrorResponse> {
  return get('/configuration/pneumatic', { pneumaticUuid }).then(jsonOrReject);
}

/**
 * Push the pnuematic with the provide uuid.
 * @param pneumaticUuid ID of the pneumatic axis
 * @param waitForMotionCompletion If true, we wait for the pushInPin of this pneumatic to be 0 before returning
 * @returns Success or error
 */
function pushPneumatic(pneumaticUuid: string, waitForMotionCompletion: boolean = false): Promise<SuccessOrError> {
  return post('/pneumatic/push', { pneumaticUuid, waitForMotionCompletion }).then(jsonOrReject);
}

/**
 * Pull the pnuematic with the provide uuid.
 * @param pneumaticUuid ID of the pneumatic axis
 * @param waitForMotionCompletion If true, we wait for the pullInPin of this pneumatic to be 0 before returning
 * @returns Success or error
 */
function pullPneumatic(pneumaticUuid: string, waitForMotionCompletion: boolean = false) {
  return post('/pneumatic/pull', { pneumaticUuid, waitForMotionCompletion }).then(jsonOrReject);
}

/**
 * Idle the pnuematic with the provide uuid.
 * @param pneumaticUuid ID of the pneumatic axis
 * @returns Success or error
 */
function idlePneumatic(pneumaticUuid: string) {
  return post('/pneumatic/idle', { pneumaticUuid }).then(jsonOrReject);
}

export default {
  getSmartDriveStatus,
  tryClearDriveErrors,
  isMotorDetected,
  getSpeed,
  getMaxSpeed,
  setSpeed,
  getAcceleration,
  setAcceleration,
  moveRelative,
  moveRelativeCombined,
  moveToPosition,
  moveToPositionCombined,
  home,
  homeAll,
  end,
  setContinuousMove,
  stopContinuousMove,
  getAxisPosition,
  setAxesPositions,
  stop,
  halt,
  isMotionComplete,
  isAxisMotionComplete,
  waitForMotionCompletion,
  setEndstopConfig,
  getEndstopState,
  getEndStopStateForAxis,
  getEndstopConfig,
  getBrakeState,
  lockBrakes,
  unlockBrakes,
  unlockAllBrakes,
  getIoState,
  writeOutput,
  writeDigitalOutputRaw,
  writePowerSwitchRaw,
  readInput,
  getAllPositionsForController,
  getAllCurrentSpeedsForController,
  loadPath,
  startPath,
  stopPath,
  parseGCode,
  getPathStatus,
  addPathTool,
  getPathTool,
  getAllPathTools,
  deletePathTool,
  setPathAxisMap,
  getPathAxisMap,
  getConfiguration,
  pushPneumatic,
  pullPneumatic,
  idlePneumatic,
  getAxisDetails,
  getInputDetails,
  getOutputDetails,
  getPneumaticDetails
};
